import { Button, Divider, Menu, MenuItem, Stack, Theme, Typography, useMediaQuery } from "@mui/material";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { MdsAdd, MdsExpandMore } from "@sinch/icons";
import { isNil } from "ramda";
import React, { FC, ReactElement, useState } from "react";
import { ArrayPath, FieldArray, FieldValues, useFieldArray, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "../MdsIcon/MdsIcon";
import { Requirements as RequirementsQueryDefinition } from "./queries.graphql";
import { RequirementsQuery } from "./queries.types";
import { RequirementTableDesktop } from "./RequirementsTableDesktop";
import { RequirementTableMobile } from "./RequirementTableMobile";
import { StaffCount } from "@sinch/components/PositionsRequirements/StaffCount";
import { PositionRequirementFormFields } from "@sinch/components";

interface AddPositionButtonProps {
  /**
   * Click event, call with profession id
   */
  onClick: (professionId: string) => void;
  /**
   * Button label
   */
  label: string;
}

/**
 * Button for creating position with selected profession
 */
const AddRequirementButton: FC<AddPositionButtonProps> = ({ onClick, label }) => {
  const { data: requirementsData } = useQueryHandler<RequirementsQuery>(RequirementsQueryDefinition);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClick = (positionId: string) => {
    setAnchorEl(null);
    onClick(positionId);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <>
      <Button
        endIcon={<MdsIcon icon={MdsExpandMore} />}
        fullWidth={false}
        onClick={openMenu}
        startIcon={<MdsIcon icon={MdsAdd} />}
        variant="text"
      >
        {label}
      </Button>
      <Menu anchorEl={anchorEl} onClose={handleClose} open={open}>
        {requirementsData?.requirements?.map(({ id, label: requirementLabel }) => (
          <MenuItem key={id} onClick={() => handleClick(id)}>
            {requirementLabel}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export type RequirementTableProps = {
  prefix: string;
  label: string;
  icon: ReactElement;
  secondaryLabel?: string;
} & (
  | {
      hideStaffCount?: false;
      inheritedRequirements?: PositionRequirementFormFields[];
      effectiveDate?: Date;
    }
  | {
      hideStaffCount?: true;
      inheritedRequirements?: never;
      effectiveDate?: never;
    }
);
/**
 * Table with positions created for shift
 */
export const RequirementsTable = <FormFields extends FieldValues, RequirementPath extends ArrayPath<FormFields>>({
  label,
  prefix,
  icon,
  hideStaffCount,
  secondaryLabel,
  inheritedRequirements,
  effectiveDate,
}: RequirementTableProps) => {
  const { t } = useTranslation();
  const { fields, ...fieldArrayMethods } = useFieldArray<FormFields, RequirementPath>({
    name: `${prefix}.requirements` as RequirementPath,
  });
  const [editedFieldIx, setEditedFieldIx] = useState<number | null>(null);

  const handleAdd = (requirementId: string) => {
    fieldArrayMethods.append({ requirementId: requirementId } as FieldArray<FormFields, RequirementPath>);
    setEditedFieldIx(fields.length);
  };
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1}>
          {icon}
          <Typography
            sx={{
              alignItems: "center",
              display: "flex",
              marginTop: (theme) => theme.spacing(3),
            }}
            variant="h4"
          >
            {label}
          </Typography>
          {secondaryLabel && (
            <Typography
              sx={{
                color: (theme) => theme.palette.grey["800"],
              }}
              variant="body1"
            >
              ({secondaryLabel})
            </Typography>
          )}
        </Stack>
        {!hideStaffCount && (
          <StaffCountForPosition
            effectiveDate={effectiveDate}
            inheritedRequirements={inheritedRequirements}
            prefix={prefix}
          />
        )}
      </Stack>
      {isMobile && (
        <RequirementTableMobile
          editedFieldIx={editedFieldIx}
          fieldArrayMethods={fieldArrayMethods}
          fields={fields}
          prefix={prefix}
          setEditedFieldIx={(ix) => setEditedFieldIx(ix)}
        />
      )}
      {!isMobile && (
        <RequirementTableDesktop
          editedFieldIx={editedFieldIx}
          fieldArrayMethods={fieldArrayMethods}
          fields={fields}
          prefix={prefix}
          setEditedFieldIx={(ix) => setEditedFieldIx(ix)}
        />
      )}
      {isMobile && <Divider />}
      {isNil(editedFieldIx) && (
        <Stack direction="row">
          <AddRequirementButton label={t("addRequirement")} onClick={(requirementId) => handleAdd(requirementId)} />
        </Stack>
      )}
    </Stack>
  );
};

const StaffCountForPosition = ({
  prefix,
  inheritedRequirements,
  effectiveDate,
}: {
  prefix: string;
  inheritedRequirements?: PositionRequirementFormFields[];
  effectiveDate?: Date;
}) => {
  const requirements = useWatch({
    name: `${prefix}.requirements`,
  });
  return (
    <StaffCount
      effectiveDate={effectiveDate}
      requirements={[...(inheritedRequirements ?? []), ...(requirements ?? [])]}
    />
  );
};
